<template>
	<div
		class="default-modal-wrapper"
		v-show="isOpen"
		ref="modal"
		:class="{
			'default-modal-wrapper--open': isOpenAnimation,
			'default-modal-wrapper--close': !isOpenAnimation,
		}"
	>
		<div class="default-modal__backdrop" @click="toggleModal"></div>
		<div :class="['default-modal', 'card', cardColor]">
			<button class="default-modal__close" @click="toggleModal">
				<svg
					width="18"
					height="18"
					viewBox="0 0 18 18"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						fill-rule="evenodd"
						clip-rule="evenodd"
						d="M15.2713 17.4136C15.8571 17.9994 16.8068 17.9994 17.3926 17.4136C17.9784 16.8278 17.9784 15.8781 17.3926 15.2923L11.101 9.00068L17.3926 2.7091C17.9784 2.12331 17.9784 1.17356 17.3926 0.587777C16.8068 0.00199068 15.8571 0.00199123 15.2713 0.587777L8.97969 6.87936L2.6881 0.587777C2.10232 0.00199123 1.15257 0.00199068 0.566781 0.587777C-0.0190054 1.17356 -0.0190049 2.12331 0.566781 2.7091L6.85837 9.00068L0.566783 15.2923C-0.0190033 15.8781 -0.0190033 16.8278 0.566783 17.4136C1.15257 17.9994 2.10232 17.9994 2.6881 17.4136L8.97969 11.122L15.2713 17.4136Z"
						fill="black"
					/>
				</svg>
			</button>
			<h2 class="default-modal__title" v-if="title">{{ title }}</h2>
			<div class="default-modal__content" v-if="content" v-html="content" />
			<div
				v-if="sections"
				v-for="(section, index) in sections"
				:key="index"
				class="default-modal__sections"
			>
				<div
					:key="index"
					class="default-modal__section"
					v-html="section.fields.text.value"
				/>
			</div>

			<DefaultButton
				v-if="ctaLink?.url"
				:cta-link="ctaLink"
				:icon="ctaIcon"
				:color="ctaColor"
				:gtm="ctaGtm"
				:disabled="ctaDisabled"
				:aria-label="ctaAriaLabel"
				@button-click="toggleModal"
			/>
			<DefaultButton
				v-if="secondaryCtaLink?.url"
				:cta-link="secondaryCtaLink"
				:icon="secondaryCtaIcon"
				:color="secondaryCtaColor"
				:gtm="secondaryCtaGtm"
				:disabled="secondaryCtaDisabled"
				:aria-label="secondaryCtaAriaLabel"
				@button-click="toggleModal"
			/>
			<p class="default-modal__tooltip" v-if="tooltip">
				{{ tooltip }}
			</p>
		</div>
	</div>
</template>
<script setup>
const fields = ref({});
const title = computed(() => fields.value?.title?.value);
const content = computed(() => fields.value?.content?.value);
const sections = computed(() => fields.value?.sections);
const tooltip = computed(() => fields.value?.tooltip?.value);
const cardColor = computed(() => fields.cardColor?.value);
const ctaLink = computed(() => fields.value?.ctaLink?.value);
const ctaIcon = computed(() => fields.value?.ctaIcon?.value);
const ctaDisabled = computed(() => fields.value?.ctaDisabled?.value);
const ctaAriaLabel = computed(() => fields.value?.ctaAriaLabel?.value);
const ctaColor = computed(() => fields.value?.ctaColor?.value);
const ctaGtm = computed(() => fields.value?.ctaGtm?.value);
const secondaryCtaLink = computed(() => fields.value?.secondaryCtaLink?.value);
const secondaryCtaIcon = computed(() => fields.value?.secondaryCtaIcon?.value);
const secondaryCtaDisabled = computed(
	() => fields.value?.secondaryCtaDisabled?.value
);
const secondaryCtaAriaLabel = computed(
	() => fields.value?.secondaryCtaAriaLabel?.value
);
const secondaryCtaColor = computed(
	() => fields.value?.secondaryCtaColor?.value
);
const secondaryCtaGtm = computed(() => fields.value?.secondaryCtaGtm?.value);

const isOpen = ref(false);
const isOpenAnimation = ref(false);

onMounted(() => {
	document.addEventListener("open-modal", toggleModal);
});

onUnmounted(() => {
	document.removeEventListener("open-modal", toggleModal);
});

function toggleModal(e = null) {
	isOpenAnimation.value = !isOpenAnimation.value;

	if (!isOpenAnimation.value) {
		setTimeout(() => {
			document.body.classList.remove("overflow-hidden");
			isOpen.value = false;
			fields.value = e?.detail?.fields || {};
		}, 500);
	} else {
		document.body.classList.add("overflow-hidden");
		isOpen.value = true;
		fields.value = e?.detail?.fields || {};
	}
}
</script>

<style lang="scss">
@keyframes modal-in {
	0% {
		transform: translateY(100%);
	}
	100% {
		transform: translateY(0);
	}
}

@keyframes modal-out {
	0% {
		transform: translateY(0);
	}
	100% {
		transform: translateY(100%);
	}
}

.default-modal-wrapper {
	position: fixed;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	z-index: 1000;

	&.default-modal-wrapper--open {
		animation: fade-in 0.5s;
		animation-fill-mode: both;
	}

	&.default-modal-wrapper--close {
		animation: fade-out 0.5s;
		animation-fill-mode: both;
	}
}

.default-modal__backdrop {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
}

.default-modal {
	max-height: 100svh;
	overflow-y: auto;
	padding: var(--spacing-6, 48px) var(--spacing-2, 16px) var(--spacing-5, 40px)
		var(--spacing-2, 16px);
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	width: 100%;
	display: flex;
	flex-direction: column;
	background-color: var(--color-white, #fff);
	border-radius: 16px 16px 0 0;
	margin: 0 auto;
	max-width: var(--app-width, 600px);
	font-family: var(--primary-font, sans-serif);
	z-index: 97;

	.default-modal-wrapper--open & {
		animation: modal-in 0.5s;
		animation-fill-mode: both;
	}

	.default-modal-wrapper--close & {
		animation: modal-out 0.5s;
		animation-fill-mode: both;
	}

	.default-modal__title {
		font-size: var(--text-xl, 24px);
		font-weight: 300;
		line-height: 120%;
		padding-bottom: var(--spacing-2, 16px);
		margin-bottom: var(--spacing-2, 16px);
		border-bottom: 1px solid #5c5c5c;
	}

	&:not(:has(.default-modal__content)) .default-modal__title {
		padding-bottom: 0;
		border-bottom: none;
	}

	p {
		font-size: var(--text-base, 16px);
		font-weight: 300;
		line-height: 140%;
		color: #767676;
	}

	strong,
	a {
		font-size: var(--text-lg, 20px);
		font-weight: 700;
		line-height: 120%;
		color: #282828;
	}

	a {
		font-size: var(--text-base, 16px);
	}

	.default-modal__close {
		background: var(--color-black, #000);
		border-radius: 50%;
		border: 0;
		position: absolute;
		right: var(--spacing-2, 16px);
		top: var(--spacing-2, 16px);
		width: 56px;
		height: 56px;
		display: flex;
		align-items: center;
		justify-content: center;

		&:hover {
			cursor: pointer;
			opacity: 0.8;
		}
	}

	.default-modal__close svg {
		width: 20px;
		height: 20px;
		path {
			fill: var(--color-white, #fff);
		}
	}

	.default-modal__tooltip {
		text-align: center;
		font-size: var(--text-xs, 12px);
		font-weight: 300;
		line-height: 128%;
	}

	.default-modal__section {
		margin: var(--spacing-1, 8px) 0;
	}

	.cta-btn {
		margin-top: var(--spacing-105, 12px);
	}
}
</style>
